import React, { useEffect, useState } from 'react';
import { Building2, DollarSign, FileText, Edit, Check, Trash2 } from 'lucide-react';
import { RemoteSupportService } from '../../services/remoteSupportService';
import { Modal, ModalBody, ModalFooter, Input, Button, ConfirmDialog } from '../ui';

const RemoteSupportDetailsModal = ({ client, isOpen, onClose, initialEditMode = false, onClientUpdated, zIndex }) => {
  const [isEditMode, setIsEditMode] = useState(initialEditMode);
  const [formData, setFormData] = useState({
    companyName: '',
    dataCollectors: '',
    invoiceDescription: '',
    baseCost: '',
    additionalCost: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form data when client changes
  useEffect(() => {
    if (client) {
      setFormData({
        companyName: client.companyName || client.company_name || '',
        dataCollectors: client.dataCollectors || client.data_collectors || '',
        invoiceDescription: client.invoiceDescription || client.invoice_description || '',
        baseCost: client.baseCost || client.base_cost || '',
        additionalCost: client.additionalCost || client.additional_cost || '',
        notes: client.notes || ''
      });
    }
  }, [client]);

  // Reset edit mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsEditMode(initialEditMode);
    }
  }, [isOpen, initialEditMode]);

  // Calculate total cost
  const calculateTotalCost = () => {
    const base = parseFloat(formData.baseCost) || 0;
    const additional = parseFloat(formData.additionalCost) || 0;
    return (base + additional).toFixed(2);
  };

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setErrors({});
    // Reset form data to original client data
    if (client) {
      setFormData({
        companyName: client.companyName || client.company_name || '',
        dataCollectors: client.dataCollectors || client.data_collectors || '',
        invoiceDescription: client.invoiceDescription || client.invoice_description || '',
        baseCost: client.baseCost || client.base_cost || '',
        additionalCost: client.additionalCost || client.additional_cost || '',
        notes: client.notes || ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (formData.baseCost && isNaN(parseFloat(formData.baseCost))) {
      newErrors.baseCost = 'Must be a valid number';
    }

    if (formData.additionalCost && isNaN(parseFloat(formData.additionalCost))) {
      newErrors.additionalCost = 'Must be a valid number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const updatedClient = await RemoteSupportService.updateClient(client.id, formData);

      setIsEditMode(false);
      setErrors({});

      if (onClientUpdated) {
        onClientUpdated(updatedClient);
      }
    } catch (error) {
      console.error('Error updating remote support client:', error);
      setErrors({ submit: 'Failed to update client. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleDelete = () => {
    if (!client?.id) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!client?.id) return;

    setLoading(true);
    try {
      await RemoteSupportService.deleteClient(client.id);

      // Close both modals and trigger refresh
      setShowDeleteConfirm(false);
      if (onClientUpdated) {
        onClientUpdated(null); // Signal that client was deleted
      }
      onClose();
    } catch (error) {
      console.error('Error deleting remote support client:', error);
      setErrors({ submit: 'Failed to delete client. Please try again.' });
      setShowDeleteConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  if (!client) return null;

  const displayCompanyName = client.companyName || client.company_name || 'Unknown Client';
  const displayDataCollectors = client.dataCollectors || client.data_collectors || '';
  const displayInvoiceDescription = client.invoiceDescription || client.invoice_description || '';
  const displayBaseCost = client.baseCost || client.base_cost || '';
  const displayAdditionalCost = client.additionalCost || client.additional_cost || '';
  const displayTotalCost = client.totalCost || client.total_cost || '';
  const displayNotes = client.notes || '';

  return (
    <>
      <Modal trackAs="RemoteSupportDetailsModal" isOpen={isOpen} onClose={onClose} size="lg" zIndex={zIndex}>
        {/* Custom Header */}
        <div
          style={{
            padding: 'var(--space-6)',
            borderBottom: '1px solid var(--gray-700)',
            background: 'linear-gradient(to bottom, var(--dark-bg), var(--dark-surface))',
            borderTopLeftRadius: 'var(--radius-xl)',
            borderTopRightRadius: 'var(--radius-xl)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: 'var(--radius-full)',
                backgroundColor: 'var(--cta-blue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Building2 size={28} color="white" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-50)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isEditMode ? formData.companyName || 'Unknown Client' : displayCompanyName}
              </h1>
              <p
                style={{
                  margin: 0,
                  marginTop: 'var(--space-1)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--gray-400)',
                }}
              >
                Remote Support Client
              </p>
            </div>
          </div>
        </div>

        <ModalBody>
          {/* Error message */}
          {errors.submit && (
            <div
              style={{
                padding: 'var(--space-3)',
                marginBottom: 'var(--space-4)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid var(--danger)',
                borderRadius: 'var(--radius-md)',
                color: 'var(--danger)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {errors.submit}
            </div>
          )}

          {/* Two column layout */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-6)' }}>
            {/* Left Column: Company Information */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Company Information
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Company Name */}
                {isEditMode ? (
                  <Input
                    label="Company Name"
                    name="companyName"
                    type="text"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    error={errors.companyName}
                    required
                    icon={<Building2 size={16} />}
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <Building2 size={14} />
                      Company Name
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {displayCompanyName}
                    </div>
                  </div>
                )}

                {/* Data Collectors */}
                {isEditMode ? (
                  <Input
                    label="Data Collectors"
                    name="dataCollectors"
                    type="text"
                    value={formData.dataCollectors}
                    onChange={handleChange}
                    placeholder="Enter data collectors"
                  />
                ) : displayDataCollectors ? (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Data Collectors
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {displayDataCollectors}
                    </div>
                  </div>
                ) : null}

                {/* Invoice Description */}
                {isEditMode ? (
                  <div>
                    <label
                      htmlFor="invoiceDescription"
                      style={{
                        display: 'block',
                        marginBottom: 'var(--space-2)',
                        color: 'var(--gray-300)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-medium)',
                      }}
                    >
                      Invoice Description
                    </label>
                    <textarea
                      id="invoiceDescription"
                      name="invoiceDescription"
                      value={formData.invoiceDescription}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Enter invoice description..."
                      style={{
                        width: '100%',
                        padding: 'var(--space-3)',
                        backgroundColor: 'var(--dark-elevated)',
                        border: '1px solid var(--gray-600)',
                        borderRadius: 'var(--radius-md)',
                        color: 'var(--gray-200)',
                        fontSize: 'var(--text-sm)',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                        transition: 'border-color var(--transition-fast)',
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = 'var(--cta-blue)';
                        e.target.style.outline = 'none';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = 'var(--gray-600)';
                      }}
                    />
                  </div>
                ) : displayInvoiceDescription ? (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <FileText size={14} />
                      Invoice Description
                    </label>
                    <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                      {displayInvoiceDescription}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Right Column: Pricing & Notes */}
            <div>
              <h2
                style={{
                  margin: 0,
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-semibold)',
                  color: 'var(--gray-200)',
                }}
              >
                Pricing
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {/* Base Cost */}
                {isEditMode ? (
                  <Input
                    label="Base Cost"
                    name="baseCost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.baseCost}
                    onChange={handleChange}
                    placeholder="0.00"
                    error={errors.baseCost}
                    icon={<DollarSign size={16} />}
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <DollarSign size={14} />
                      Base Cost
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {displayBaseCost ? `$${displayBaseCost}` : '—'}
                    </div>
                  </div>
                )}

                {/* Additional Cost */}
                {isEditMode ? (
                  <Input
                    label="Additional Cost"
                    name="additionalCost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.additionalCost}
                    onChange={handleChange}
                    placeholder="0.00"
                    error={errors.additionalCost}
                    icon={<DollarSign size={16} />}
                  />
                ) : (
                  <div
                    style={{
                      padding: 'var(--space-4)',
                      backgroundColor: 'var(--dark-surface)',
                      border: '1px solid var(--gray-700)',
                      borderRadius: 'var(--radius-lg)',
                    }}
                  >
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      <DollarSign size={14} />
                      Additional Cost
                    </label>
                    <div style={{ color: 'var(--gray-100)', fontSize: 'var(--text-base)', fontWeight: 'var(--font-medium)' }}>
                      {displayAdditionalCost ? `$${displayAdditionalCost}` : '—'}
                    </div>
                  </div>
                )}

                {/* Total Cost */}
                <div
                  style={{
                    padding: 'var(--space-4)',
                    backgroundColor: 'var(--dark-surface)',
                    border: '1px solid var(--gray-700)',
                    borderRadius: 'var(--radius-lg)',
                  }}
                >
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <DollarSign size={14} />
                    Total Cost
                  </label>
                  <div style={{ color: 'var(--success)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)' }}>
                    ${isEditMode ? calculateTotalCost() : (displayTotalCost || '0.00')}
                  </div>
                </div>

                {/* Notes */}
                {(displayNotes || isEditMode) && (
                  isEditMode ? (
                    <div>
                      <label
                        htmlFor="notes"
                        style={{
                          display: 'block',
                          marginBottom: 'var(--space-2)',
                          color: 'var(--gray-300)',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                        }}
                      >
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Additional notes..."
                        style={{
                          width: '100%',
                          padding: 'var(--space-3)',
                          backgroundColor: 'var(--dark-elevated)',
                          border: '1px solid var(--gray-600)',
                          borderRadius: 'var(--radius-md)',
                          color: 'var(--gray-200)',
                          fontSize: 'var(--text-sm)',
                          fontFamily: 'inherit',
                          resize: 'vertical',
                          transition: 'border-color var(--transition-fast)',
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--cta-blue)';
                          e.target.style.outline = 'none';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--gray-600)';
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--dark-surface)',
                        border: '1px solid var(--gray-700)',
                        borderRadius: 'var(--radius-lg)',
                      }}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)', color: 'var(--gray-400)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-medium)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Notes
                      </label>
                      <div style={{ color: 'var(--gray-300)', fontSize: 'var(--text-sm)', lineHeight: 'var(--leading-relaxed)' }}>
                        {displayNotes || 'No notes'}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </ModalBody>

        {/* Footer with Actions */}
        <ModalFooter>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {isEditMode ? (
              <>
                {/* Left: Delete Button */}
                <Button variant="danger" size="md" onClick={handleDelete} disabled={loading} icon={<Trash2 size={16} />}>
                  Delete Client
                </Button>

                {/* Right: Cancel & Save */}
                <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                  <Button variant="ghost" size="md" onClick={handleCancelEdit} disabled={loading}>
                    Cancel
                  </Button>
                  <Button variant="primary" size="md" onClick={handleSave} loading={loading} icon={<Check size={16} />}>
                    Save Changes
                  </Button>
                </div>
              </>
            ) : (
              <>
                {/* Empty left side for balance */}
                <div></div>

                {/* Right: Edit Button */}
                <Button variant="secondary" size="md" onClick={handleEdit} icon={<Edit size={16} />}>
                  Edit Client
                </Button>
              </>
            )}
          </div>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Remote Support Client"
        message={`Are you sure you want to delete ${displayCompanyName}? This action cannot be undone and will permanently remove this client from the remote support program.`}
        confirmText="Delete Client"
        cancelText="Cancel"
        variant="danger"
        loading={loading}
      />
    </>
  );
};

export default RemoteSupportDetailsModal;
